import {
  Box,
  Button,
  Checkbox,
  CrudMode,
  DataArray,
  DataDisplay,
  formatBoolean,
  formatDate,
  formatMinutes,
  formatTime,
  GeneralValidationErrors,
  Grid,
  GridBreak,
  pn,
  Stack,
  Typography,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import EscalatorWarningIcon from "@mui/icons-material/EscalatorWarning";
import { DatePicker, DurationInput, FilePanel, Input, Select, TimePicker } from "components";
import {
  LkStudentServiceDisplayFieldId,
  Permission,
  School,
  StudentService,
  StudentServiceAdditionalEntry,
  StudentServiceCreateBulk,
  StudentServiceEditResource,
  StudentServiceViewResource,
  UploadFile,
} from "generated/openApi";
import produce from "immer";
import moment from "moment";
import "moment-timezone";
import { providerCreatePageRoute } from "pages/providers/providers/ProviderCreatePage/ProviderCreatePage";
import { collegePageRoute } from "pages/system/utility/colleges/CollegePage/CollegePage";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { AdditionalDates } from "./AdditionalDates";
import { AdditionalEntries } from "./AdditionalEntries/AdditionalEntries";
import { ServiceFiles } from "./ServiceFiles/ServiceFiles";
import { StudentSignature } from "./StudentSignature";
import { InfoIcon } from "components/common/InfoIcon";

export const StudentServicePageBody = (props: {
  rootPermission: Permission;
  crudMode: CrudMode;
  service: StudentService;
  setService: (value: StudentService) => void;
  viewResource: StudentServiceViewResource;
  editResource: StudentServiceEditResource;
  validationErrors: ValidationErrors | string[];
  onEdit?: () => void;
  hideStudent?: boolean;
  additionalEntries?: StudentServiceAdditionalEntry[];
  setAdditionalEntries?: (value: StudentServiceAdditionalEntry[]) => void;
  additionalDates?: Date[];
  setAdditionalDates?: (value: Date[]) => void;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, mediumFieldBreakpoints, largeFieldBreakpoints, fullFieldBreakpoints } =
    useElementBreakpoints();
  const properties = props.viewResource.properties || [];
  const generationValidationErrors = props.validationErrors[pn<StudentService>("generalValidationErrors")];
  const navigate = useNavigate();

  const [studentSchools, setStudentSchools] = useState<School[]>([]);
  const [durationHoursWarning, setDurationHoursWarning] = useState(false);
  const [futureDateWarning, setFutureDateWarning] = useState(false);
  const [studentSignatureOpen, setStudentSignatureOpen] = useState(false);
  const [staffSignatureOpen, setStaffSignatureOpen] = useState(false);

  const [splitDuration, setSplitDuration] = useState(false);

  const id = props.service.id;

  const serviceTypes = props.viewResource.serviceTypes?.filter(
    (st) =>
      st.schools?.some((school) => studentSchools.map((y) => y.id).includes(school.id)) ||
      !st.schools ||
      st.schools.length === 0
  );

  const selectedServiceType = props.viewResource.serviceTypes?.find((x) => x.id === props.service.serviceTypeId);
  const serviceSubTypes =
    props.viewResource.serviceSubTypes?.filter((x) => x.serviceTypeId === props.service.serviceTypeId) || [];

  const displayFields = props.viewResource.displayFields || [];
  const durationDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Duration);
  const startTimeDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.StartTime);
  const endTimeDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.EndTime);
  const serviceLocationDisplayField = displayFields.find(
    (x) => x.id === LkStudentServiceDisplayFieldId.ServiceLocation
  );
  const languageDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Language);
  const followUpDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.FollowUp);
  const isServiceDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.IsService);
  const referralDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Referral);
  const signatureDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Signature);
  const staffUserDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.StaffUser);
  const roomDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Room);
  const nameDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Name);
  const modalityDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Modality);
  const splitDurationDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.SplitDuration);
  const flagForReviewDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.FlagForReview);
  const topicsDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Topics);
  const staffSignatureDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.StaffSignature);

  // get the service types for the student based on schools
  useEffect(() => {
    if (props.crudMode === "edit") {
      if (props.service.studentId && props.service.studentId > 0)
        ApiService.studentServices.getStudentSchools(props.service.studentId).then((response) => {
          setStudentSchools(response);
        });
      else {
        setStudentSchools([]);
      }
    }
  }, [props.service.studentId]);

  useEffect(() => {
    updateSplitDurations();
  }, [splitDuration]);

  const addProvider = () => {
    navigate({
      pathname: `./${providerCreatePageRoute.getPath({ screenType: "dialog" })}`,
      search: providerCreatePageRoute.getOptionalParams?.({
        closeOnSave: true,
      }),
    });
  };

  const addCollege = () => {
    navigate({
      pathname: `./${collegePageRoute.getPath({ id: 0, crudMode: "edit", screenType: "dialog" })}`,
      search: collegePageRoute.getOptionalParams?.({
        closeOnSave: true,
      }),
    });
  };

  const studentField = () => {
    const propertyName = pn<StudentService>("studentId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        {props.service.student && (
          <>
            {props.service.student.lastName}, {props.service.student.firstName}
          </>
        )}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.service.student}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        onChange={(e, value) => {
          props.setService(
            produce(props.service, (draft) => {
              draft.studentId = value?.id;
              draft.student = value;
            })
          );
        }}
        fetchFunction={(searchString: string) => ApiService.search.students(searchString)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const flagForReviewField = () => {
    const propertyName = pn<StudentService>("flagForReview");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.service.flagForReview)}</DataDisplay>
    ) : (
      <Checkbox
        label={property?.displayName}
        checked={props.service.flagForReview}
        onChange={(e) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.flagForReview = e.target.checked;
            })
          )
        }
      />
    );
  };

  const splitDurationField = () => {
    return (
      <Checkbox label="Split Duration" checked={splitDuration} onChange={(e) => setSplitDuration(!splitDuration)} />
    );
  };

  const dateField = () => {
    const propertyName = pn<StudentService>("date");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.service.date)}</DataDisplay>
    ) : (
      <Stack>
        <DatePicker
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.service.date}
          onChange={(value) => {
            props.setService(
              produce(props.service, (draft) => {
                draft.date = value;
              })
            );

            const isValidDate = moment(value).isValid();
            const isFutureDate = moment(value).isAfter(moment());
            setFutureDateWarning(isValidDate && isFutureDate);
          }}
        />
        {futureDateWarning && (
          <Typography color={(theme) => theme.palette.warning.main} variant="caption">
            {property?.displayName} is in the future.
          </Typography>
        )}
      </Stack>
    );
  };

  const serviceTypeField = () => {
    const propertyName = pn<StudentService>("serviceTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <Stack direction="row" spacing="sm" alignItems="center">
          <Typography variant="inherit">{props.service.serviceType?.name}</Typography>
          {props.service.serviceType?.isForParent && <EscalatorWarningIcon />}
        </Stack>
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={serviceTypes?.find((x) => x.id === props.service.serviceTypeId)}
        options={serviceTypes}
        groupBy={(option) => (option.isForParent ? "Parent" : "Student")}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.serviceTypeId = value?.id;
              draft.serviceSubTypeId = undefined;
              draft.parentCount = undefined;
            })
          )
        }
      />
    );
  };

  const serviceSubTypeField = () => {
    if (!serviceSubTypes || serviceSubTypes.length === 0) return null;

    const propertyName = pn<StudentService>("serviceSubTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.service.serviceSubType?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        required
        errors={props.validationErrors[propertyName]}
        value={serviceSubTypes?.find((x) => x.id === props.service.serviceSubTypeId)}
        options={serviceSubTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.serviceSubTypeId = value?.id;
            })
          )
        }
      />
    );
  };

  const parentCountField = () => {
    const propertyName = pn<StudentService>("parentCount");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.service.parentCount}</DataDisplay>
    ) : (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Input
          modelProperty={property}
          required
          errors={props.validationErrors[propertyName]}
          value={props.service.parentCount}
          onChange={(e) =>
            props.setService(
              produce(props.service, (draft) => {
                draft.parentCount = parseFloat(e.target.value);
              })
            )
          }
        />
        <InfoIcon text="Number of Parents Per Student" />
      </Stack>
    );
  };

  const nameField = () => {
    const propertyName = pn<StudentService>("name");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.service.name}</DataDisplay>
    ) : (
      <Select
        freeSolo
        modelProperty={property}
        required={selectedServiceType?.isNameMandatory}
        errors={props.validationErrors[propertyName]}
        inputValue={props.service.name}
        fetchFunction={(searchString: string) => ApiService.search.studentServiceNames(searchString)}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.name = value?.name;
            })
          )
        }
        onInputChange={(e) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.name = e.target.value;
            })
          )
        }
      />
    );
  };

  const updateSplitDurations = () => {
    if (splitDuration && props.service.durationInMinutes != null) {
      if (props.additionalEntries != undefined && props.additionalEntries.length > 0) {
        let splitAmount = (props.service.durationInMinutes / (props.additionalEntries.length + 1));
        splitAmount = Math.ceil(Math.round(splitAmount * 100) / 100);

        props.setAdditionalEntries!(
          produce(props.additionalEntries, (draft) => {
            draft.forEach((entry) => {
              entry.durationInMinutes = splitAmount!;
            });
          })
        );

        props.setService(
          produce(props.service, (draft) => {
            draft.durationInMinutes = splitAmount!;
          })
        );
      }
    }
  }

  const durationField = () => {
    const propertyName = pn<StudentService>("durationInMinutes");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatMinutes(props.service.durationInMinutes)}</DataDisplay>
    ) : (
      <Stack>
        <DurationInput
          required={durationDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={props.service.durationInMinutes}
          onBlur={() => updateSplitDurations()}
          onChange={(value) => {
            props.setService(
              produce(props.service, (draft) => {
                draft.durationInMinutes = value!;
              })
            );

            if (value && value > 8 * 60) setDurationHoursWarning(true);
            else setDurationHoursWarning(false);
          }}
        />
        {durationHoursWarning && (
          <Typography color={(theme) => theme.palette.warning.main} variant="caption">
            {property?.displayName} is longer than 8 hours.
          </Typography>
        )}
      </Stack>
    );
  };

  const startTimeField = () => {
    const propertyName = pn<StudentService>("startTime");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatTime(props.service.startTime)}</DataDisplay>
    ) : (
      <TimePicker
        modelProperty={property}
        required={startTimeDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.service.startTime}
        onChange={(value) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.startTime = value;
            })
          )
        }
      />
    );
  };

  const endTimeField = () => {
    const propertyName = pn<StudentService>("endTime");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatTime(props.service.endTime)}</DataDisplay>
    ) : (
      <TimePicker
        modelProperty={property}
        required={endTimeDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.service.endTime}
        onChange={(value) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.endTime = value;
            })
          )
        }
      />
    );
  };

  const collegesField = () => {
    const propertyName = pn<StudentService>("colleges");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <DataArray value={props.service.colleges?.map((x) => x.name!)} />
      </DataDisplay>
    ) : (
      <Select
        multiple
        modelProperty={property}
        required={serviceLocationDisplayField?.required || selectedServiceType?.isServiceLocationMandatory}
        errors={props.validationErrors[propertyName]}
        value={props.service.colleges}
        options={props.editResource.colleges}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.colleges = values;
            })
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText={<Button onClick={addCollege}>+ New College</Button>}
      />
    );
  };

  const schoolField = () => {
    const propertyName = pn<StudentService>("schoolId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.service.school?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        required={serviceLocationDisplayField?.required || selectedServiceType?.isServiceLocationMandatory}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schools?.find((x) => x.id === props.service.schoolId)}
        options={props.editResource.schools}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.schoolId = value?.id;
            })
          )
        }
      />
    );
  };

  const providersField = () => {
    const propertyName = pn<StudentService>("providers");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <DataArray value={props.service.providers?.map((x) => `${x.lastName}, ${x.firstName}`)} />
      </DataDisplay>
    ) : (
      <Select
        multiple
        modelProperty={property}
        required={selectedServiceType?.isProviderMandatory}
        errors={props.validationErrors[propertyName]}
        value={props.service.providers}
        options={props.editResource.providers}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        onChange={(e, values) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.providers = values;
            })
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText={<Button onClick={addProvider}>+ New Provider</Button>}
      />
    );
  };

  const referralTopicsField = () => {
    const propertyName = pn<StudentService>("referralTopics");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <DataArray value={props.service.referralTopics?.filter(x => x.isReferral).map((x) => `${x.name}`)} />
      </DataDisplay>
    ) : (
      <Select
        multiple
        modelProperty={property}
        required={topicsDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.service.referralTopics}
        options={props.editResource.topics?.filter(x => x.isReferral)}
        getOptionLabel={(option) => `${option.name}`}
        onChange={(e, values) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.referralTopics = values;
            })
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const serviceTopicsField = () => {
    const propertyName = pn<StudentService>("topics");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <DataArray value={props.service.topics?.filter(x => x.isService).map((x) => `${x.name}`)} />
      </DataDisplay>
    ) : (
      <Select
        multiple
        modelProperty={property}
        required={topicsDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.service.topics}
        options={props.editResource.topics?.filter(x => x.isService)}
        getOptionLabel={(option) => `${option.name}`}
        onChange={(e, values) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.topics = values;
            })
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const languageField = () => {
    const propertyName = pn<StudentService>("languageId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.service.language?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        required={languageDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.languages?.find((x) => x.id === props.service.languageId)}
        options={props.editResource.languages}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.languageId = value?.id;
            })
          )
        }
      />
    );
  };

  const modalityField = () => {
    const propertyName = pn<StudentService>("modalityId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.service.modality?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        required={selectedServiceType?.isModalityMandatory}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.modalities?.find((x) => x.id === props.service.modalityId)}
        options={props.editResource.modalities}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.modalityId = value?.id;
            })
          )
        }
      />
    );
  };

  const noteField = () => {
    const propertyName = pn<StudentService>("note");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName} type="textarea">
        {props.service.note}
      </DataDisplay>
    ) : (
      <Input
        multiline
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.service.note}
        onChange={(e) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.note = e.target.value;
            })
          )
        }
      />
    );
  };

  const followUpField = () => {
    const propertyName = pn<StudentService>("followUp");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName} type="textarea">
        {props.service.followUp}
      </DataDisplay>
    ) : (
      <Input
        multiline
        modelProperty={property}
        required={followUpDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.service.followUp}
        onChange={(e) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.followUp = e.target.value;
            })
          )
        }
      />
    );
  };

  const referralField = () => {
    const propertyName = pn<StudentService>("referral");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.service.referral)}</DataDisplay>
    ) : (
      <Checkbox
        label={property?.displayName}
        checked={props.service.referral}
        onChange={(e) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.referral = e.target.checked;
            })
          )
        }
      />
    );
  };

  const studentSignatureField = () => {
    const propertyName = pn<StudentService>("studentSignatureUploadFileId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <>
        {props.service.studentSignatureUploadFile?.deleted === false ? (
          <FilePanel
            label={property?.displayName}
            fileName={props.service.studentSignatureUploadFile?.fileName}
            fileRef={props.service.studentSignatureUploadFile?.fileRef}
            fileSize={props.service.studentSignatureUploadFile?.fileSize}
            uploadingData={props.service.studentSignatureUploadFile?.uploadingData}
            imagePreviewData={props.service.studentSignatureUploadFile?.imagePreviewData}
            delete={() => {
              props.setService(
                produce(props.service, (draft) => {
                  draft.studentSignatureUploadFile!.deleted = true;
                })
              );
              props.onEdit?.();
            }}
          />
        ) : (
          <DataDisplay label={property?.displayName}>
            <Button onClick={() => setStudentSignatureOpen(true)}>+ Signature</Button>
          </DataDisplay>
        )}
      </>
    );
  };

  const staffSignatureField = () => {
    const propertyName = pn<StudentService>("staffSignatureUploadFileId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <>
        {props.service.staffSignatureUploadFile?.deleted === false ? (
          <FilePanel
            label={property?.displayName}
            fileName={props.service.staffSignatureUploadFile?.fileName}
            fileRef={props.service.staffSignatureUploadFile?.fileRef}
            fileSize={props.service.staffSignatureUploadFile?.fileSize}
            uploadingData={props.service.staffSignatureUploadFile?.uploadingData}
            imagePreviewData={props.service.staffSignatureUploadFile?.imagePreviewData}
            delete={() => {
              props.setService(
                produce(props.service, (draft) => {
                  draft.staffSignatureUploadFile!.deleted = true;
                })
              );
              props.onEdit?.();
            }}
          />
        ) : (
          <DataDisplay label={property?.displayName}>
            <Button onClick={() => setStaffSignatureOpen(true)}>+ Signature</Button>
          </DataDisplay>
        )}
      </>
    );
  };

  const staffNameField = () => {
    const propertyName = pn<StudentService>("staffName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={property?.displayName}>{props.service.staffName}</DataDisplay>;
  };

  const isServiceField = () => {
    const propertyName = pn<StudentService>("isService");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.service.isService)}</DataDisplay>
    ) : (
      <Checkbox
        label={property?.displayName}
        checked={props.service.isService}
        onChange={(e) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.isService = e.target.checked;
            })
          )
        }
      />
    );
  };

  const staffUserField = () => {
    const propertyName = pn<StudentService>("staffUserId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        {props.service.staffUser && (
          <>
            {props.service.staffUser.lastName}, {props.service.staffUser.firstName}
          </>
        )}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        required={staffUserDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.service.staffUser}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        options={props.editResource.staffUsers}
        onChange={(e, value) => {
          props.setService(
            produce(props.service, (draft) => {
              draft.staffUserId = value?.id;
              draft.staffUser = value;
            })
          );
        }}
      />
    );
  };

  const roomField = () => {
    const propertyName = pn<StudentService>("room");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName} type="textarea">
        {props.service.room}
      </DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        required={roomDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.service.room}
        onChange={(e) =>
          props.setService(
            produce(props.service, (draft) => {
              draft.room = e.target.value;
            })
          )
        }
      />
    );
  };

  return (
    <Stack spacing="md">
      <Grid ref={breakpointsRef} container spacing="sm">
        {generationValidationErrors && (
          <Grid item xs={fullFieldBreakpoints}>
            <GeneralValidationErrors errors={generationValidationErrors} />
          </Grid>
        )}

        {!props.hideStudent && (
          <Grid item xs={smallFieldBreakpoints}>
            {studentField()}
          </Grid>
        )}

        <Grid item xs={smallFieldBreakpoints}>
          {dateField()}
        </Grid>

        {flagForReviewDisplayField && (
          <Grid item xs={smallFieldBreakpoints}>
            {flagForReviewField()}
          </Grid>
        )}

        {props.additionalDates !== undefined && props.setAdditionalDates !== undefined && props.service.id === 0 && (
          <Grid item xs={smallFieldBreakpoints}>
            <Stack direction="row" alignItems="center" height="100%">
              <AdditionalDates
                dates={props.additionalDates || []}
                setDates={(values) => props.setAdditionalDates!(values)}
              />
            </Stack>
          </Grid>
        )}

        <GridBreak />

        <Grid item xs={smallFieldBreakpoints}>
          {serviceTypeField()}
        </Grid>

        {serviceSubTypes && serviceSubTypes.length > 0 && (
          <Grid item xs={smallFieldBreakpoints}>
            {serviceSubTypeField()}
          </Grid>
        )}

        {selectedServiceType?.isForParent && (
          <Grid item xs={smallFieldBreakpoints}>
            {parentCountField()}
          </Grid>
        )}

        {durationDisplayField && (
          <Grid item xs={smallFieldBreakpoints}>
            {durationField()}
          </Grid>
        )}

        {splitDurationDisplayField && props.additionalEntries != undefined && props.additionalEntries?.length > 0 && (
          <Grid item xs={smallFieldBreakpoints}>
            {splitDurationField()}
          </Grid>
        )}

        {serviceLocationDisplayField && (
          <>
            {selectedServiceType?.aprStudentService?.isServiceLocationForCollege ? (
              <Grid item xs={mediumFieldBreakpoints}>
                {collegesField()}
              </Grid>
            ) : (
              <Grid item xs={smallFieldBreakpoints}>
                {schoolField()}
              </Grid>
            )}
          </>
        )}

        <GridBreak />

        {referralDisplayField && (
          <Grid item xs={smallFieldBreakpoints}>
            {referralField()}
          </Grid>
        )}

        {topicsDisplayField && referralDisplayField && props.service.referral && (
          <Grid item xs={mediumFieldBreakpoints}>
            {referralTopicsField()}
          </Grid>
        )}
        <GridBreak />

        {isServiceDisplayField && (
          <Grid item xs={smallFieldBreakpoints}>
            {isServiceField()}
          </Grid>
        )}

        {topicsDisplayField && isServiceDisplayField && props.service.isService && (
          <Grid item xs={mediumFieldBreakpoints}>
            {serviceTopicsField()}
          </Grid>
        )}

        <GridBreak />

        {props.additionalEntries !== undefined &&
          props.setAdditionalEntries !== undefined &&
          props.service.id === 0 && (
            <Grid item xs={12}>
              <Box>
                <AdditionalEntries
                  originalService={props.service}
                  entries={props.additionalEntries || []}
                  setEntries={(values) => props.setAdditionalEntries!(values)}
                  viewResource={props.viewResource}
                  editResource={props.editResource}
                  serviceTypes={serviceTypes || []}
                  validationErrors={props.validationErrors[pn<StudentServiceCreateBulk>("additionalEntries")] || {}}
                />
              </Box>
            </Grid>
          )}

        <GridBreak />

        {nameDisplayField && (
          <Grid item xs={smallFieldBreakpoints}>
            {nameField()}
          </Grid>
        )}

        {startTimeDisplayField && (
          <Grid item xs={smallFieldBreakpoints}>
            {startTimeField()}
          </Grid>
        )}

        {endTimeDisplayField && (
          <Grid item xs={smallFieldBreakpoints}>
            {endTimeField()}
          </Grid>
        )}

        {languageDisplayField && (
          <Grid item xs={smallFieldBreakpoints}>
            {languageField()}
          </Grid>
        )}

        {modalityDisplayField && (
          <Grid item xs={smallFieldBreakpoints}>
            {modalityField()}
          </Grid>
        )}

        <GridBreak />

        <Grid item xs={largeFieldBreakpoints}>
          {providersField()}
        </Grid>

        <Grid item xs={fullFieldBreakpoints}>
          {noteField()}
        </Grid>

        {followUpDisplayField && (
          <Grid item xs={fullFieldBreakpoints}>
            {followUpField()}
          </Grid>
        )}

        <GridBreak />

        {staffUserDisplayField && (
          <Grid item xs={smallFieldBreakpoints}>
            {staffUserField()}
          </Grid>
        )}

        {roomDisplayField && (
          <Grid item xs={smallFieldBreakpoints}>
            {roomField()}
          </Grid>
        )}

        <GridBreak />

        {signatureDisplayField && (
          <Grid item xs={smallFieldBreakpoints}>
            {studentSignatureField()}
          </Grid>
        )}

        {staffSignatureDisplayField && (
          <>
            {props.service.staffName && (
              <Grid item xs={smallFieldBreakpoints}>
                {staffNameField()}
              </Grid>
            )}

            <Grid item xs={smallFieldBreakpoints}>
              {staffSignatureField()}
            </Grid>
          </>
        )}
      </Grid>

      <ServiceFiles
        rootPermission={props.rootPermission}
        id={id}
        serviceFiles={props.service.files || []}
        setServiceFiles={(serviceFiles) => {
          props.setService(
            produce(props.service, (draft) => {
              draft.files = serviceFiles;
            })
          );
          props.onEdit?.();
        }}
      />

      <StudentSignature
        type="Student"
        open={studentSignatureOpen}
        close={() => setStudentSignatureOpen(false)}
        signatureFile={props.service.studentSignatureUploadFile || ({} as UploadFile)}
        done={(value) => {
          props.setService({ ...props.service, studentSignatureUploadFile: value });
          setStudentSignatureOpen(false);
          props.onEdit?.();
        }}
      />

      <StudentSignature
        type="Staff"
        open={staffSignatureOpen}
        close={() => setStaffSignatureOpen(false)}
        signatureFile={props.service.staffSignatureUploadFile || ({} as UploadFile)}
        done={(value) => {
          props.setService({ ...props.service, staffSignatureUploadFile: value });
          setStaffSignatureOpen(false);
          props.onEdit?.();
        }}
      />
    </Stack>
  );
};
